import { XMarkIcon } from "@heroicons/react/24/solid";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { isNullOrWhiteSpace } from "../app/utils";
import { handleSpaceKey } from "../app/keyboardUtils";

export enum AlertLevel {
	Error,
	Warning,
	Info,
}

interface Props {
	className?: string;
	type: AlertLevel;
	alertId?: string;
	message?: string | null;
	showIcon?: boolean;
	onClear?: (key?: string, message?: string | null) => void;
}

export default function AlertMessage(props: Props) {
	function handleClearAlert() {
		if (props.onClear) props.onClear(props.alertId, props.message);
	}

	const wrapperClassName = (() => {
		switch (props.type) {
			case AlertLevel.Error:
				return "border border-red-400 bg-red-100 text-red-700";
			case AlertLevel.Warning:
				return "border border-yellow-200 bg-yellow-50 text-yellow-800";
			case AlertLevel.Info:
				return "border border-green-400 bg-green-100 text-green-800";
		}
	})();

	const closeIconClassName = (() => {
		switch (props.type) {
			case AlertLevel.Error:
				return "text-red-500";
			case AlertLevel.Warning:
				return "text-yellow-600";
			case AlertLevel.Info:
				return "text-gray-700";
		}
	})();

	interface AlertIconProps {
		className: string;
	}

	const AlertIcon = ({ className }: AlertIconProps) => {
		const classes = "h-7 w-7";
		switch (props.type) {
			case AlertLevel.Error:
				return (
					<ExclamationTriangleIcon
						className={`${className} ${classes} text-red-600`}
						aria-hidden="true"
					/>
				);
			case AlertLevel.Warning:
				return (
					<ExclamationCircleIcon
						className={`${className} ${classes} text-yellow-800`}
						aria-hidden="true"
					/>
				);
			case AlertLevel.Info:
				return (
					<QuestionMarkCircleIcon
						className={`${className} ${classes} text-green-600`}
						aria-hidden="true"
					/>
				);
		}
	};

	if (isNullOrWhiteSpace(props.message)) return null;

	return (
		<div
			className={`${props.className} ${wrapperClassName} pl-6 p-4 rounded-sm flex justify-between`}
			role="alert">
			<div className="flex justify-between items-center">
				<div>{props.showIcon && <AlertIcon className="mr-3" />}</div>
				<div className="whitespace-pre-line leading-6">{props.message}</div>
			</div>
			{props.onClear && (
				<button
					className="h-6 w-6 shrink-0"
					onClick={handleClearAlert}
					onKeyDown={(e) => handleSpaceKey(e, handleClearAlert)}>
					<XMarkIcon className={`${closeIconClassName}`}></XMarkIcon>
				</button>
			)}
		</div>
	);
}
